<script setup>
import { computed, onMounted, ref } from 'vue'
import { api, asList } from '../api/client'
import { actorName, centsToMoney, formatCurrency, sumActiveSales } from '../api/commerce'
import { formatDateTime } from '../utils'
import AppIcon from '../components/AppIcon.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import SaleDialog from '../components/SaleDialog.vue'
import SaleCancelDialog from '../components/SaleCancelDialog.vue'
import { useToast } from '../composables/useToast'

const { show } = useToast()
const sales = ref([])
const catalog = ref([])
const loading = ref(true)
const loadError = ref('')
const statusFilter = ref('')
const search = ref('')
const dialogOpen = ref(false)
const saving = ref(false)
const saveError = ref('')
const cancellingSale = ref(null)
const cancelling = ref(false)
const cancelError = ref('')
const visibleSales = computed(() => sales.value.filter((sale) => {
  const term = search.value.trim().toLocaleLowerCase('ru')
  const searchable = `#${sale.id} ${sale.booking_id ? `бронь #${sale.booking_id}` : ''} ${sale.notes || ''} ${actorName(sale.created_by)} ${sale.lines.map((line) => line.item_name).join(' ')}`
  return (!statusFilter.value || sale.status === statusFilter.value) && (!term || searchable.toLocaleLowerCase('ru').includes(term))
}).sort((a, b) => b.id - a.id))
const activeTotal = computed(() => centsToMoney(sumActiveSales(visibleSales.value)))

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    const [rows, products] = await Promise.all([api.sales.list(), api.catalog.list()])
    sales.value = asList(rows)
    catalog.value = asList(products)
  } catch (error) { loadError.value = error.message || 'Не удалось загрузить продажи.' }
  finally { loading.value = false }
}

function openCreate() { saveError.value = ''; dialogOpen.value = true }

async function createSale(payload) {
  if (saving.value) return
  saving.value = true
  saveError.value = ''
  try {
    await api.sales.create(payload)
    dialogOpen.value = false
    show('Продажа проведена, товары списаны со склада.')
    await load()
  } catch (error) { saveError.value = error.message || 'Не удалось провести продажу.' }
  finally { saving.value = false }
}

function openCancel(sale) { cancelError.value = ''; cancellingSale.value = sale }

async function cancelSale(reason) {
  if (!cancellingSale.value || cancelling.value) return
  cancelling.value = true
  cancelError.value = ''
  try {
    await api.sales.cancel(cancellingSale.value.id, reason)
    cancellingSale.value = null
    show('Продажа отменена, товары возвращены на склад. История сохранена.')
    await load()
  } catch (error) { cancelError.value = error.message || 'Не удалось отменить продажу.' }
  finally { cancelling.value = false }
}

onMounted(load)
</script>

<template>
  <div class="page sales-page">
    <header class="page-header"><div><span class="eyebrow">Дополнительные продажи</span><h1>Продажи</h1><p class="page-subtitle">Товары и услуги отдельно или в составе брони. Проведённые записи можно только отменить с причиной, не удалить.</p></div><button class="button button--primary" type="button" :disabled="loading" @click="openCreate"><AppIcon name="plus" :size="18" />Новая продажа</button></header>
    <section class="filter-bar" aria-label="Фильтры продаж">
      <label class="search-field"><AppIcon name="search" :size="18" /><span class="sr-only">Поиск продаж</span><input v-model="search" type="search" placeholder="Товар, автор, номер продажи или брони" /></label>
      <label class="compact-field"><span class="sr-only">Статус продажи</span><select v-model="statusFilter"><option value="">Все статусы</option><option value="completed">Проведённые</option><option value="cancelled">Отменённые</option></select></label>
      <button class="button button--secondary" type="button" :disabled="loading" @click="load">Обновить</button>
    </section>
    <LoadingState v-if="loading" />
    <section v-else-if="loadError" class="data-section"><p class="form-error" role="alert">{{ loadError }}</p><button class="button button--secondary" type="button" @click="load">Повторить</button></section>
    <section v-else class="data-section">
      <header class="section-header"><div><h2>История продаж</h2><p>По фильтру: {{ visibleSales.length }} · проведено на {{ formatCurrency(activeTotal) }}</p></div></header>
      <div v-if="visibleSales.length" class="table-scroll"><table class="data-table"><caption class="sr-only">История продаж товаров и услуг</caption><thead><tr><th>Продажа</th><th>Состав</th><th>Сумма</th><th>Статус</th><th><span class="sr-only">Действия</span></th></tr></thead><tbody>
        <tr v-for="sale in visibleSales" :key="sale.id" :class="{ 'row--muted': sale.status === 'cancelled' }">
          <td data-label="Продажа"><strong>№{{ sale.id }}</strong><span class="cell-secondary">{{ formatDateTime(sale.created_at) }} · {{ actorName(sale.created_by) }}</span><RouterLink v-if="sale.booking_id" :to="`/bookings?booking=${sale.booking_id}`" class="text-link">Бронь №{{ sale.booking_id }}</RouterLink><span v-else class="cell-secondary">Без привязки к брони</span></td>
          <td data-label="Состав"><div v-for="line in sale.lines" :key="line.id" class="sale-summary-line">{{ line.item_name }}<span class="cell-secondary">{{ line.quantity }} {{ line.item_unit }} × {{ formatCurrency(line.unit_price) }} = {{ formatCurrency(line.total_amount) }}</span></div><span v-if="sale.notes" class="cell-secondary">{{ sale.notes }}</span></td>
          <td data-label="Сумма"><strong>{{ formatCurrency(sale.total_amount) }}</strong></td>
          <td data-label="Статус"><span class="availability" :class="sale.status === 'cancelled' ? 'availability--inactive' : 'availability--occupied'">{{ sale.status === 'cancelled' ? 'Отменена' : 'Проведена' }}</span><span v-if="sale.status === 'cancelled'" class="cell-secondary">{{ formatDateTime(sale.cancelled_at) }} · {{ actorName(sale.cancelled_by) }}<br />{{ sale.cancel_reason }}</span></td>
          <td class="row-actions"><button v-if="sale.status !== 'cancelled'" class="button button--small button--ghost" type="button" :aria-label="`Отменить продажу ${sale.id}`" @click="openCancel(sale)">Отменить</button><span v-else class="cell-secondary">История сохранена</span></td>
        </tr>
      </tbody></table></div>
      <EmptyState v-else title="Продаж пока нет" text="Добавьте продажу товаров или услуг. Продажи к гостю также доступны в карточке брони." />
    </section>
    <SaleDialog :open="dialogOpen" :catalog="catalog" :saving="saving" :error="saveError" @close="!saving && (dialogOpen = false)" @save="createSale" />
    <SaleCancelDialog :open="Boolean(cancellingSale)" :sale="cancellingSale" :pending="cancelling" :error="cancelError" @close="!cancelling && (cancellingSale = null)" @confirm="cancelSale" />
  </div>
</template>

<style scoped>.sale-summary-line + .sale-summary-line { margin-top: 8px; }</style>
